import * as React from 'react';
import { Lock, LogIn, FileText } from 'lucide-react';
import { cn } from '../lib/utils.js';
import { Button } from './ui/button.js';
import { Input } from './ui/input.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card.js';

export interface LoginFormProps {
  /** Callback when login is submitted */
  onLogin: (password: string) => Promise<void>;
  /** Error message to display */
  error?: string | null;
  /** Whether login is in progress */
  isLoading?: boolean;
  /** Additional class name */
  className?: string;
  /** Title for the login form */
  title?: string;
  /** Description for the login form */
  description?: string;
}

/**
 * LoginForm provides an elegant password login interface.
 */
export function LoginForm({
  onLogin,
  error = null,
  isLoading = false,
  className,
  title = 'Log Collector',
  description = 'Enter your admin password to access the dashboard',
}: LoginFormProps) {
  const [password, setPassword] = React.useState('');
  const [localError, setLocalError] = React.useState<string | null>(null);

  const displayError = error || localError;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (!password.trim()) {
      setLocalError('Password is required');
      return;
    }

    try {
      await onLogin(password);
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Login failed');
    }
  };

  return (
    <div
      className={cn(
        'relative flex min-h-screen items-center justify-center p-4',
        'bg-[var(--color-background)]',
        className
      )}
    >
      {/* Subtle gradient background */}
      <div
        className="pointer-events-none absolute inset-0 overflow-hidden"
        aria-hidden="true"
      >
        <div
          className="absolute -left-1/4 -top-1/4 h-[600px] w-[600px] rounded-full opacity-20"
          style={{
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, transparent 70%)',
          }}
        />
        <div
          className="absolute -bottom-1/4 -right-1/4 h-[500px] w-[500px] rounded-full opacity-20"
          style={{
            background: 'radial-gradient(circle, rgba(139, 92, 246, 0.15) 0%, transparent 70%)',
          }}
        />
      </div>

      {/* Login Card */}
      <Card
        className={cn(
          'relative z-10 w-full max-w-[400px]',
          'border-[var(--color-border)] bg-[var(--color-card)]/95 backdrop-blur-xl',
          'shadow-[0_8px_32px_rgba(0,0,0,0.3)]'
        )}
      >
        <CardHeader className="space-y-4 pb-4 text-center">
          {/* Logo/Icon */}
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-xl bg-[var(--color-primary)]/10 shadow-[0_0_20px_rgba(59,130,246,0.15)]">
            <FileText className="h-7 w-7 text-[var(--color-primary)]" />
          </div>
          <div className="space-y-1">
            <CardTitle className="text-2xl font-semibold tracking-tight text-[var(--color-foreground)]">
              {title}
            </CardTitle>
            <CardDescription className="text-[var(--color-muted-foreground)]">
              {description}
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className="pb-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Error Message */}
            {displayError && (
              <div
                className={cn(
                  'flex items-center gap-2 rounded-lg border px-4 py-3 text-sm',
                  'border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]',
                  'animate-[fade-in_0.2s_ease-out]'
                )}
              >
                <svg
                  className="h-4 w-4 shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>{displayError}</span>
              </div>
            )}

            {/* Password Input */}
            <div className="space-y-2">
              <label
                htmlFor="password"
                className="text-sm font-medium text-[var(--color-foreground)]"
              >
                Admin Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted-foreground)]" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  autoComplete="current-password"
                  disabled={isLoading}
                  autoFocus
                  className="pl-10"
                />
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="h-11 w-full text-base font-medium"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Authenticating...
                </>
              ) : (
                <>
                  <LogIn className="h-4 w-4" />
                  Sign In
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default LoginForm;
